import {
  Alert,
  Button,
  FormControl,
  IconButton,
  OutlinedInput,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import WhiteCard from "../WhiteCard";
import { FormEvent, useCallback, useMemo, useState } from "react";
import {
  useCreateClinicalNoteMutation,
  useGetPatientClinicalNotesQuery,
} from "src/api/services/clinicalNotesApi";
import { PatientProfile } from "src/api/types";
import { useClinician } from "src/context/SessionProvider/ClinicianProvider";
import { OpenWith } from "@mui/icons-material";
import { isNonProviderView } from "src/utils/isNonProviderView";
import { useLocation, useNavigate } from "react-router-dom";
import Note from "./components/Note";

interface ClinicalNoteProps {
  patient: PatientProfile;
  noteType: "Coordinator" | "Provider";
  order?: number;
  maxHeight?: string | number;
  noteTitle: string;
  onSuccess?: () => Promise<void>;
  backgroundColor?: string;
}

interface AlertProps {
  display: boolean;
  message: string;
  severity: "success" | "error";
  onSuccess?: () => Promise<void>;
}

const ClinicalNote = ({
  patient,
  noteType,
  order,
  maxHeight = "200px",
  noteTitle,
  onSuccess,
  backgroundColor = "inherit",
}: ClinicalNoteProps) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const [createNote] = useCreateClinicalNoteMutation();
  const { data: notes = [] } = useGetPatientClinicalNotesQuery({
    patientId: patient.id,
    noteType,
  });
  const [note, setNote] = useState<string>("");
  const [alert, setAlert] = useState<AlertProps>({
    display: false,
    message: "",
    severity: "success",
  });
  const isNPV = useMemo(() => {
    return isNonProviderView(pathname);
  }, [pathname]);

  const clinicianId = useClinician((c) => c.id);
  const roles = useClinician((c) => c.roles);

  const handleExpand = useCallback(() => {
    return navigate(`notes/${noteType}`);
  }, [navigate, noteType]);

  const handleCreateNote = useCallback(
    async (e: FormEvent<HTMLFormElement>) => {
      e.preventDefault();

      if (clinicianId == null) {
        setAlert({
          display: true,
          message: "Clinician ID is missing",
          severity: "error",
        });
        return;
      }

      try {
        await createNote({
          patient_id: patient.id,
          profile_id: clinicianId,
          note: note,
          note_type: noteType,
        })
          .unwrap()
          .then(() => {
            setAlert({
              display: true,
              message: "Successfully created clinical note",
              severity: "success",
            });
            setNote("");
            onSuccess?.();
          });
      } catch {
        setAlert({
          display: true,
          message:
            "Looks like there was an error when trying to create the clinical note. Please try again",
          severity: "error",
        });
      } finally {
        setTimeout(
          () =>
            setAlert({
              display: false,
              message: "",
              severity: "success",
            }),
          3000,
        );
      }
    },
    [clinicianId, createNote, note, noteType, onSuccess, patient.id],
  );

  const canCreateNote = useMemo(() => {
    if (roles.length === 0) return false;
    return roles.some((r) => r.name.includes(noteType));
  }, [noteType, roles]);

  return (
    <WhiteCard gap={1} order={order} sx={{ backgroundColor: backgroundColor }}>
      <Stack direction={"row"} justifyContent={"space-between"}>
        <Typography variant="subtitle2">{noteTitle} NOTES</Typography>
        {isNPV && !pathname.includes("notes") ? (
          <Tooltip title="Expand" sx={{ marginLeft: "auto" }}>
            <IconButton onClick={handleExpand}>
              <OpenWith />
            </IconButton>
          </Tooltip>
        ) : null}
      </Stack>
      {canCreateNote ? (
        <Stack component="form" onSubmit={handleCreateNote} gap={1}>
          <FormControl>
            <OutlinedInput
              value={note}
              onChange={(e) => setNote(e.target.value)}
              id="outlined-adornment"
              type="text"
              multiline
            />
          </FormControl>
          {alert.display ? (
            <Alert variant="filled" severity={alert.severity}>
              {alert.message}
            </Alert>
          ) : null}
          <Button
            disabled={note.length === 0}
            sx={{ alignSelf: "end", width: "fit-content" }}
            type="submit"
          >
            Submit
          </Button>
        </Stack>
      ) : null}
      {notes.length > 0 ? (
        <Stack maxHeight={maxHeight} overflow="auto" gap={2}>
          {notes.map((n, idx) => (
            <Note note={n} idx={idx} noteType={noteType} key={n.id} />
          ))}
        </Stack>
      ) : null}
    </WhiteCard>
  );
};

export default ClinicalNote;
